import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  KNOWN_FEATURE_KEYS,
  type FeatureKind,
  type FeatureRow,
} from '@/modules/admin/catalog/feature-limits';

/**
 * The feature limits on a plan: `{"servers": 10}`, `{"backups": true}`, `{"monitors": -1}`.
 *
 * ## Why this is not a JSON textarea
 *
 * It could be. It would be a tenth of the code, and it would be wrong in a specific way:
 * `servers` is the entitlement that decides whether a customer can add a machine at all, so a
 * typo here — `server`, or `"10"` as a string — produces a plan that looks published and
 * grants nothing. The customer sees **0 / 0 servers** and no error anywhere explains it.
 *
 * A row-based editor makes the shape unmisspellable in the common case (`servers` is offered)
 * and makes the *kind* of each limit explicit rather than inferred from how somebody typed it.
 *
 * ## Three kinds of limit, because the domain has three
 *
 * A number is a quota. `-1` is unlimited — a magic value, so it gets its own choice rather
 * than expecting somebody to know the convention. A boolean is a capability that is either
 * present or not. Collapsing these into one free-text field is how `-1` ends up meaning
 * "minus one servers".
 */

export function FeatureEditor({
  rows,
  onChange,
}: {
  rows: FeatureRow[];
  onChange: (rows: FeatureRow[]) => void;
}) {
  const update = (index: number, patch: Partial<FeatureRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const usedKeys = new Set(rows.map((r) => r.key));
  const suggestions = KNOWN_FEATURE_KEYS.filter((k) => !usedKeys.has(k));

  return (
    <div className="space-y-3">
      <div>
        <Label>Entitlements</Label>
        <p className="mt-1 text-xs text-muted-foreground">
          What a subscription on this plan grants.{' '}
          <span className="font-medium text-foreground">
            Without a <code className="font-mono">servers</code> entitlement a customer cannot
            add any machines
          </span>
          . The platform will look broken to them.
        </p>
      </div>

      {rows.length === 0 && (
        <p className="rounded-md border border-dashed px-3 py-4 text-center text-sm text-muted-foreground">
          No entitlements yet.
        </p>
      )}

      {rows.map((row, index) => (
        <div key={index} className="flex flex-wrap items-end gap-2">
          <div className="min-w-[9rem] flex-1">
            <Input
              value={row.key}
              onChange={(e) => update(index, { key: e.target.value })}
              list="feature-keys"
              className="font-mono text-sm"
              aria-label="Entitlement name"
            />
          </div>

          <div className="w-36">
            <Select
              value={row.kind}
              onChange={(e) => update(index, { kind: e.target.value as FeatureKind })}
              aria-label="Limit type"
            >
              <option value="quota">a number</option>
              <option value="unlimited">unlimited</option>
              <option value="boolean">yes / no</option>
            </Select>
          </div>

          {row.kind === 'quota' && (
            <div className="w-24">
              <Input
                value={row.quota}
                onChange={(e) => update(index, { quota: e.target.value })}
                inputMode="numeric"
                aria-label="Limit"
              />
            </div>
          )}

          {row.kind === 'boolean' && (
            <div className="w-24">
              <Select
                value={row.enabled ? 'yes' : 'no'}
                onChange={(e) => update(index, { enabled: e.target.value === 'yes' })}
                aria-label="Enabled"
              >
                <option value="yes">yes</option>
                <option value="no">no</option>
              </Select>
            </div>
          )}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(rows.filter((_, i) => i !== index))}
            aria-label={`Remove ${row.key || 'entitlement'}`}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ))}

      {/* Native datalist: the known keys are suggestions, not a closed set — a future module
          will add its own entitlement and must not need this file edited to be usable. */}
      <datalist id="feature-keys">
        {KNOWN_FEATURE_KEYS.map((key) => (
          <option key={key} value={key} />
        ))}
      </datalist>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...rows, { key: '', kind: 'quota', quota: '', enabled: true }])}
        >
          <Plus className="mr-1.5 size-3.5" />
          Add entitlement
        </Button>

        {suggestions.map((key) => (
          <Button
            key={key}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() =>
              onChange([...rows, { key, kind: 'quota', quota: '', enabled: true }])
            }
          >
            + {key}
          </Button>
        ))}
      </div>
    </div>
  );
}
